import Image from "@/components/common/Image";
import { CreateAdLink } from "@/components/HowItWorks/SellingCreateAdLink";
import { SellingFAQ } from "@/components/HowItWorks/SellingFAQ";
import { SellingSection } from "@/components/HowItWorks/SellingSection";
import { HorizontalSplitter } from "@/ui/HorizontalSplitter";
import { useTranslation } from "next-i18next";
import { SEO } from "../common";
import { LokaliseMarkdownText } from "@/components/text/LokaliseMarkdownText";
import { Heading } from "@/ui/cva/Heading";

type SellingProps = {
  faq?: Record<string, unknown>;
};

const Selling = ({ faq }: SellingProps) => {
  const { t } = useTranslation();

  return (
    <div className="flex flex-col space-y-10 px-4 py-10 md:px-6 lg:px-12 xl:px-24">
      <SEO title={t("selling:seo_how_it_works_selling_page_title")} />

      <Heading as="h3" className="text-center">
        {t("selling:selling-title")}
      </Heading>
      <CreateAdLink showIcon className="md:mb-10 md:self-center" />
      <HorizontalSplitter />
      {/* Block 1 */}
      <SellingSection>
        <div className="flex flex-col items-start space-y-6 self-center">
          <LokaliseMarkdownText
            lokaliseKey="selling:selling-block-1-v2"
            className="flex flex-col space-y-4"
          />
          <CreateAdLink outlined />
        </div>
        <div className="flex flex-col space-y-6">
          <LokaliseMarkdownText
            lokaliseKey="selling:selling-block-1-list"
            className="flex flex-col space-y-4"
            overrides={{
              ul: {
                props: {
                  className: "m-0 flex flex-col space-y-4",
                },
              },
              li: {
                props: {
                  className:
                    "list-none bg-flavour-100 p-4 rounded-[0.375rem] text-brand-500 [&>img]:float-left [&>img]:mr-2.5",
                },
              },
            }}
          />
        </div>
      </SellingSection>
      <HorizontalSplitter />
      {/* Block 2 */}
      <SellingSection>
        <div className="aspect-h-3 aspect-w-4 relative order-1 md:order-none">
          <Image
            src={t("selling:selling-block-2-image-v2")}
            layout="fill"
            objectFit="contain"
            alt="Selling block 2"
          />
        </div>
        <LokaliseMarkdownText
          lokaliseKey="selling:selling-block-2-v2"
          className="flex flex-col space-y-4 self-center"
        />
      </SellingSection>
      <HorizontalSplitter />
      {/* Block 5 */}
      <SellingSection>
        <LokaliseMarkdownText
          lokaliseKey="selling:selling-block-5"
          className="flex flex-col space-y-4 self-center"
          overrides={{
            a: {
              props: {
                target: "_blank",
                className: "text-brand-500 underline",
              },
            },
            img: {
              props: {
                className: "w-56",
              },
            },
          }}
        />
        <div className="aspect-h-3 aspect-w-4 relative order-1 md:order-none">
          <Image
            src={t("selling:selling-block-5-image")}
            layout="fill"
            objectFit="contain"
            alt="Selling block 2"
          />
        </div>
      </SellingSection>
      <HorizontalSplitter />
      {/* Block 6 */}
      <SellingSection>
        <div className="aspect-h-3 aspect-w-4 relative order-1 md:order-none">
          <Image
            src={t("selling:selling-block-6-image")}
            layout="fill"
            objectFit="contain"
            alt="Selling block 4"
          />
        </div>
        <div className="flex flex-col items-start space-y-6 self-center">
          <LokaliseMarkdownText
            lokaliseKey="selling:selling-block-6"
            className="flex flex-col space-y-4"
          />
        </div>
      </SellingSection>
      <HorizontalSplitter />
      {/* Block 3 */}
      <SellingSection>
        <LokaliseMarkdownText
          lokaliseKey="selling:selling-block-3-v2"
          className="grid gap-6 md:col-span-2 md:grid-cols-2 [&>:first-child]:text-center [&>:first-child]:md:col-span-2 [&>p>span>img]:mr-1 [&>p>span]:flex [&>p>span]:items-center [&>p]:flex [&>p]:flex-col [&>p]:space-y-2 [&>p]:rounded-[0.375rem] [&>p]:border [&>p]:border-brand-300 [&>p]:p-6"
        />
      </SellingSection>
      <HorizontalSplitter />
      {/* Block 4 */}
      <SellingSection>
        <div className="aspect-h-3 aspect-w-4 relative order-1 md:order-none">
          <Image
            src={t("selling:selling-block-4-image-v2")}
            layout="fill"
            objectFit="contain"
            alt="Selling block 4"
          />
        </div>
        <div className="flex flex-col items-start space-y-6 self-center">
          <LokaliseMarkdownText
            lokaliseKey="selling:selling-block-4-v2"
            className="flex flex-col space-y-4"
          />
          <CreateAdLink />
        </div>
      </SellingSection>
      <HorizontalSplitter />
      <SellingFAQ faq={faq} />
    </div>
  );
};

export default Selling;
